import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import {jwtDecode} from "jwt-decode"
import axios from "axios"
import {useNavigate} from "react-router-dom"

type DecodedToken = {
    id: string
    flags?: string[]
    exp?: number
}

export type AuthContextValue = {
    getGates: () => string[]
    hasGate: (name: string) => boolean
    hasPermission: (name: string) => boolean
    hasAnyPermission: (names: string[]) => boolean
    isActualAdmin: () => boolean
    rolePreview: RolePreview
    setRolePreview: (role: RolePreview) => void
    logOutLocally: () => void
    logOutGlobally: () => void
    axiosAuthHeader: () => {
        headers?: {
            Authorization: string
        }
    }
    impersonate: (jwt: string) => void
    recoverSession: () => void
    sessionRecovery: boolean
    isAuthenticated: () => boolean
    login: (redirectTo?: string) => void
    fetchToken: () => Promise<boolean>
}

export type RolePreview =
    | "examOfficeEmployee"
    | "examOfficeAssistant"
    | "dean"
    | "infrastructure"
    | "facultyOwner"
    | "deputy"
    | "disabilityOfficer"
    | "reader"
    | null

const JWT_STORAGE_KEY = "jwt"
const JWT_RECOVER_STORAGE_KEY = "jwt-recover"
const IMPERSONATION_EXPIRY_STORAGE_KEY =
    "impersonation-expires-at"
const LOCAL_LOGOUT_STORAGE_KEY = "local-logout"
const LOGIN_REDIRECT_STORAGE_KEY = "login-redirect"
const ROLE_PREVIEW_STORAGE_KEY = "admin-role-preview"

const ROLE_PREVIEW_GATES: Record<Exclude<RolePreview, null>, string[]> = {
    examOfficeEmployee: ["examOfficeEmployee"],
    examOfficeAssistant: ["examOfficeAssistant"],
    dean: ["dean"],
    infrastructure: ["infrastructure"],
    facultyOwner: ["examManagement", "facultyOwner"],
    deputy: ["examManagement", "deputy"],
    disabilityOfficer: ["examManagement", "disabilityOfficer"],
    reader: ["examManagement"],
}

const SSO_KEEP_ALIVE_INTERVAL_MS =
    15 * 60 * 1000

const JWT_RENEW_INTERVAL_MS =
    60 * 60 * 1000

const IMPERSONATION_DURATION_MS =
    60 * 60 * 1000

const JWT_RENEW_BEFORE_EXPIRY_SECONDS =
    10 * 60

const AuthContext = createContext<AuthContextValue>(
    {} as AuthContextValue
)

export const useAuth = () => {
    return useContext(AuthContext)
}

export const AuthProvider = ({
                                 children,
                             }: {
    children: React.ReactNode
}) => {
    const nav = useNavigate()

    const [decoded, setDecoded] =
        useState<DecodedToken | null>(() => {
            const stored = localStorage.getItem(JWT_STORAGE_KEY)
            if (!stored || stored === "undefined" || stored === "null") {
                return null
            }
            try {
                return jwtDecode<DecodedToken>(stored)
            } catch {
                localStorage.removeItem(JWT_STORAGE_KEY)
                return null
            }
        })

    const [rolePreview, setRolePreviewState] = useState<RolePreview>(() => {
        const stored = sessionStorage.getItem(ROLE_PREVIEW_STORAGE_KEY)
        return stored && stored in ROLE_PREVIEW_GATES
            ? stored as Exclude<RolePreview, null>
            : null
    })

    const [sessionRecovery, setSessionRecovery] =
        useState<boolean>(false)

    const renewInProgressRef =
        useRef<boolean>(false)

    const tokenFetchInProgressRef =
        useRef<Promise<boolean> | null>(null)

    const ssoSessionAliveRef =
        useRef<boolean | null>(null)

    const impersonationTimeoutRef =
        useRef<number | null>(null)

    const getJwt = useCallback((): string | null => {
        const jwt =
            localStorage.getItem(
                JWT_STORAGE_KEY
            )

        if (
            !jwt ||
            jwt === "undefined" ||
            jwt === "null"
        ) {
            return null
        }

        return jwt
    }, [])

    const decodeJwt = useCallback(
        (
            jwt: string
        ): DecodedToken | null => {
            try {
                return jwtDecode<DecodedToken>(
                    jwt
                )
            } catch {
                return null
            }
        },
        []
    )

    const storeJwt = useCallback(
        (jwt: string): boolean => {
            const data = decodeJwt(jwt)

            if (!data) {
                return false
            }

            localStorage.setItem(
                JWT_STORAGE_KEY,
                jwt
            )

            setDecoded(data)

            return true
        },
        [decodeJwt]
    )

    const clearJwt = useCallback(() => {
        localStorage.removeItem(
            JWT_STORAGE_KEY
        )

        setDecoded(null)
    }, [])

    const clearImpersonationTimeout =
        useCallback(() => {
            if (
                impersonationTimeoutRef.current !==
                null
            ) {
                window.clearTimeout(
                    impersonationTimeoutRef.current
                )

                impersonationTimeoutRef.current =
                    null
            }
        }, [])

    const clearImpersonation =
        useCallback(() => {
            clearImpersonationTimeout()

            localStorage.removeItem(
                JWT_RECOVER_STORAGE_KEY
            )

            localStorage.removeItem(
                IMPERSONATION_EXPIRY_STORAGE_KEY
            )

            setSessionRecovery(false)
        }, [clearImpersonationTimeout])

    const isImpersonating =
        useCallback((): boolean => {
            return Boolean(
                localStorage.getItem(
                    JWT_RECOVER_STORAGE_KEY
                )
            )
        }, [])

    const isExpiring = useCallback(
        (data: DecodedToken): boolean => {
            if (!data.exp) {
                return true
            }

            return (
                (
                    data.exp -
                    JWT_RENEW_BEFORE_EXPIRY_SECONDS
                ) *
                1000 <
                Date.now()
            )
        },
        []
    )

    const isExpired = useCallback(
        (data: DecodedToken): boolean => {
            return (
                !data.exp ||
                data.exp * 1000 <= Date.now()
            )
        },
        []
    )

    const isAuthenticated =
        useCallback((): boolean => {
            return (
                decoded !== null &&
                getJwt() !== null
            )
        }, [decoded, getJwt])

    const login = useCallback(
        (redirectTo?: string) => {
            if (redirectTo) {
                sessionStorage.setItem(
                    LOGIN_REDIRECT_STORAGE_KEY,
                    redirectTo
                )
            }

            localStorage.removeItem(
                LOCAL_LOGOUT_STORAGE_KEY
            )

            window.location.href =
                "/api/login"
        },
        []
    )

    const axiosAuthHeader =
        useCallback(() => {
            const jwt = getJwt()

            return jwt
                ? {
                    headers: {
                        Authorization:
                            `Bearer ${jwt}`,
                    },
                }
                : {}
        }, [getJwt])

    const isActualAdmin = useCallback(
        (): boolean => (decoded?.flags ?? []).includes("admin"),
        [decoded]
    )

    const setRolePreview = useCallback((role: RolePreview) => {
        if (role) {
            sessionStorage.setItem(ROLE_PREVIEW_STORAGE_KEY, role)
        } else {
            sessionStorage.removeItem(ROLE_PREVIEW_STORAGE_KEY)
        }
        setRolePreviewState(role)
    }, [])

    useEffect(() => {
        if (decoded && !isActualAdmin() && rolePreview) {
            setRolePreview(null)
        }
    }, [decoded, isActualAdmin, rolePreview, setRolePreview])

    const getGates =
        useCallback((): string[] => {
            if (rolePreview && isActualAdmin()) {
                return ROLE_PREVIEW_GATES[rolePreview]
            }
            return decoded?.flags ?? []
        }, [decoded, isActualAdmin, rolePreview])

    const hasGate = useCallback(
        (name: string): boolean => {
            return getGates().includes(name)
        },
        [getGates]
    )

    const hasPermission = useCallback(
        (name: string): boolean => {
            return (
                hasGate("admin") ||
                hasGate(name)
            )
        },
        [hasGate]
    )

    const hasAnyPermission = useCallback(
        (names: string[]): boolean => {
            return (
                hasGate("admin") ||
                names.some(
                    (name: string) =>
                        hasGate(name)
                )
            )
        },
        [hasGate]
    )

    const logOutLocally =
        useCallback(() => {
            clearJwt()
            clearImpersonation()

            localStorage.setItem(
                LOCAL_LOGOUT_STORAGE_KEY,
                "true"
            )

            nav("/")
        }, [
            clearImpersonation,
            clearJwt,
            nav,
        ])

    const logOutGlobally =
        useCallback(() => {
            clearJwt()
            clearImpersonation()

            window.location.href =
                "/api/logout"
        }, [
            clearImpersonation,
            clearJwt,
        ])

    const recoverSession =
        useCallback(() => {
            clearImpersonationTimeout()

            const recoverJwt =
                localStorage.getItem(
                    JWT_RECOVER_STORAGE_KEY
                )

            localStorage.removeItem(
                IMPERSONATION_EXPIRY_STORAGE_KEY
            )

            if (
                !recoverJwt ||
                recoverJwt === "undefined" ||
                recoverJwt === "null"
            ) {
                localStorage.removeItem(
                    JWT_RECOVER_STORAGE_KEY
                )

                setSessionRecovery(false)

                return
            }

            const restored =
                storeJwt(recoverJwt)

            localStorage.removeItem(
                JWT_RECOVER_STORAGE_KEY
            )

            setSessionRecovery(false)

            if (!restored) {
                clearJwt()
            }
        }, [
            clearImpersonationTimeout,
            clearJwt,
            storeJwt,
        ])

    const scheduleImpersonationRecovery =
        useCallback(() => {
            clearImpersonationTimeout()

            const recoverJwt =
                localStorage.getItem(
                    JWT_RECOVER_STORAGE_KEY
                )

            if (!recoverJwt) {
                localStorage.removeItem(
                    IMPERSONATION_EXPIRY_STORAGE_KEY
                )

                setSessionRecovery(false)

                return
            }

            const expiryValue =
                localStorage.getItem(
                    IMPERSONATION_EXPIRY_STORAGE_KEY
                )

            if (!expiryValue) {
                recoverSession()
                return
            }

            const expiresAt =
                Number(expiryValue)

            const remainingMs =
                expiresAt - Date.now()

            if (
                !Number.isFinite(expiresAt) ||
                remainingMs <= 0
            ) {
                recoverSession()
                return
            }

            setSessionRecovery(true)

            impersonationTimeoutRef.current =
                window.setTimeout(() => {
                    recoverSession()
                }, remainingMs)
        }, [
            clearImpersonationTimeout,
            recoverSession,
        ])

    const impersonate = useCallback(
        (jwt: string) => {
            const currentJwt = getJwt()
            const data = decodeJwt(jwt)

            if (!currentJwt || !data) {
                return
            }

            /*
             * Preserve the original session only
             * when starting a new impersonation.
             */
            if (!isImpersonating()) {
                localStorage.setItem(
                    JWT_RECOVER_STORAGE_KEY,
                    currentJwt
                )
            }

            localStorage.setItem(
                IMPERSONATION_EXPIRY_STORAGE_KEY,
                String(
                    Date.now() +
                    IMPERSONATION_DURATION_MS
                )
            )

            localStorage.setItem(
                JWT_STORAGE_KEY,
                jwt
            )

            setDecoded(data)
            setSessionRecovery(true)

            scheduleImpersonationRecovery()
        },
        [
            decodeJwt,
            getJwt,
            isImpersonating,
            scheduleImpersonationRecovery,
        ]
    )

    const keepSSOAlive =
        useCallback(async (): Promise<boolean> => {
            try {
                const response =
                    await axios.get(
                        "/api/user/keepSSOAlive"
                    )

                const isAlive =
                    response.data?.message ===
                    "alive"

                if (!isAlive) {
                    ssoSessionAliveRef.current =
                        false

                    console.warn(
                        "[Auth] SSO session is no longer active"
                    )
                } else {
                    ssoSessionAliveRef.current =
                        true
                }

                return isAlive
            } catch (error: unknown) {
                console.error(
                    "[Auth] Failed to keep SSO session alive",
                    error
                )

                return false
            }
        }, [])

    const renewToken =
        useCallback(async (): Promise<boolean> => {
            if (renewInProgressRef.current) {
                return false
            }

            /*
             * Do not renew the impersonated JWT.
             * The original SSO session is kept alive
             * separately.
             */
            if (isImpersonating()) {
                return false
            }

            /*
             * A confirmed expired OIDC session must
             * not shorten the lifetime of the current
             * application JWT. Keep using it until its
             * own expiry, but do not extend it.
             */
            if (
                ssoSessionAliveRef.current ===
                false
            ) {
                return false
            }

            const jwt = getJwt()

            if (!jwt) {
                return false
            }

            renewInProgressRef.current = true

            try {
                const response =
                    await axios.get(
                        "/api/user/renewToken",
                        {
                            headers: {
                                Authorization:
                                    `Bearer ${jwt}`,
                            },
                        }
                    )

                if (
                    response.status !== 200 &&
                    response.status !== 202
                ) {
                    return false
                }

                const nextJwt =
                    response.data?.jwt

                if (
                    !nextJwt ||
                    !storeJwt(nextJwt)
                ) {
                    clearJwt()
                    return false
                }

                return true
            } catch (error: unknown) {
                if (
                    axios.isAxiosError(error) &&
                    error.response?.status === 401
                ) {
                    /*
                     * The backend also returns 401 when
                     * the OIDC session has ended. Retain
                     * a still valid JWT in that case.
                     */
                    ssoSessionAliveRef.current =
                        false

                    const currentData =
                        decodeJwt(jwt)

                    if (
                        !currentData ||
                        isExpired(currentData)
                    ) {
                        clearJwt()
                    }
                }

                return false
            } finally {
                renewInProgressRef.current =
                    false
            }
        }, [
            clearJwt,
            decodeJwt,
            getJwt,
            isExpired,
            isImpersonating,
            storeJwt,
        ])

    const checkTokenInLocalStorage =
        useCallback(() => {
            const jwt = getJwt()

            if (!jwt) {
                setDecoded(null)
                return
            }

            const data = decodeJwt(jwt)

            if (!data) {
                clearJwt()
                return
            }

            setDecoded(data)

            if (
                isExpiring(data) &&
                !isImpersonating()
            ) {
                void renewToken()
            }
        }, [
            clearJwt,
            decodeJwt,
            getJwt,
            isExpiring,
            isImpersonating,
            renewToken,
        ])

    const fetchToken =
        useCallback((): Promise<boolean> => {
            if (tokenFetchInProgressRef.current) {
                return tokenFetchInProgressRef.current
            }

            const request = (async (): Promise<boolean> => {
            /*
             * Never replace an active impersonation
             * with a fresh SSO token.
             */
            if (isImpersonating()) {
                checkTokenInLocalStorage()
                scheduleImpersonationRecovery()

                return true
            }

            try {
                const response =
                    await axios.get(
                        "/api/user/getToken"
                    )

                const jwt =
                    response.data?.jwt

                if (
                    !jwt ||
                    !storeJwt(jwt)
                ) {
                    clearJwt()
                    return false
                }

                const redirect =
                    sessionStorage.getItem(
                        LOGIN_REDIRECT_STORAGE_KEY
                    )

                if (redirect) {
                    sessionStorage.removeItem(
                        LOGIN_REDIRECT_STORAGE_KEY
                    )

                    const current =
                        window.location.pathname +
                        window.location.search +
                        window.location.hash

                    if (redirect !== current) {
                        nav(redirect, {replace: true})
                    }
                }

                return true
            } catch (error: unknown) {
                clearJwt()

                if (
                    axios.isAxiosError(error)
                ) {
                    if (
                        error.response?.status ===
                        401 ||
                        error.response?.status ===
                        403
                    ) {
                        return false
                    }

                    if (!error.response) {
                        console.error(
                            "Unable to contact backend."
                        )
                    }
                }

                return false
            }
            })()

            tokenFetchInProgressRef.current = request
            void request.finally(() => {
                if (tokenFetchInProgressRef.current === request) {
                    tokenFetchInProgressRef.current = null
                }
            })

            return request
        }, [
            checkTokenInLocalStorage,
            clearJwt,
            isImpersonating,
            nav,
            scheduleImpersonationRecovery,
            storeJwt,
        ])

    /*
     * Restore the JWT from local storage and
     * preserve an active impersonation after reload.
     */
    useEffect(() => {
        const recoverJwt =
            localStorage.getItem(
                JWT_RECOVER_STORAGE_KEY
            )

        if (recoverJwt) {
            setSessionRecovery(true)
            scheduleImpersonationRecovery()
        }

        checkTokenInLocalStorage()

        if (
            !getJwt() &&
            localStorage.getItem(
                LOCAL_LOGOUT_STORAGE_KEY
            ) !== "true"
        ) {
            void fetchToken()
        }

        return () => {
            clearImpersonationTimeout()
        }
    }, [
        checkTokenInLocalStorage,
        clearImpersonationTimeout,
        fetchToken,
        getJwt,
        scheduleImpersonationRecovery,
    ])

    /*
     * Keep the real OIDC session alive independently
     * of JWT renewal and impersonation.
     */
    useEffect(() => {
        if (
            localStorage.getItem(
                LOCAL_LOGOUT_STORAGE_KEY
            ) === "true"
        ) {
            return
        }

        void keepSSOAlive()

        const interval =
            window.setInterval(() => {
                void keepSSOAlive()
            }, SSO_KEEP_ALIVE_INTERVAL_MS)

        return () => {
            window.clearInterval(interval)
        }
    }, [keepSSOAlive])

    /*
     * Renew the application JWT regularly.
     * Impersonated JWTs are intentionally excluded.
     */
    useEffect(() => {
        const interval =
            window.setInterval(() => {
                if (
                    !getJwt() ||
                    isImpersonating()
                ) {
                    return
                }

                void renewToken()
            }, JWT_RENEW_INTERVAL_MS)

        return () => {
            window.clearInterval(interval)
        }
    }, [
        getJwt,
        isImpersonating,
        renewToken,
    ])

    return (
        <AuthContext.Provider
            value={{
                getGates,
                hasGate,
                hasPermission,
                hasAnyPermission,
                isActualAdmin,
                rolePreview,
                setRolePreview,
                logOutLocally,
                logOutGlobally,
                axiosAuthHeader,
                impersonate,
                recoverSession,
                sessionRecovery,
                isAuthenticated,
                login,
                fetchToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}
